/**
 * THE TOOLS THAT MOVED OFF THE TOOLBAR AND INTO THE ADMIN PANEL.
 *
 * <h2>One list, two consumers</h2>
 *
 * The toolbar hides exactly what this file names, and the panel offers exactly what this file
 * names — both read this array. Two hand-maintained lists is how a tool comes to be hidden from
 * the toolbar and absent from the panel, which is not a relocation, it is a deletion nobody meant
 * and nobody can see. {@code adminPanelTools.test.js} asserts the two stay in step.
 *
 * <h2>The panel INVOKES through the toolbar's own dispatcher</h2>
 *
 * Each entry carries the same {@code action} string the toolbar button carried, and the panel
 * calls {@code handleIconClick(action)} with it. So a relocated tool does literally the same thing
 * it did before — there is no second invocation path to drift, and a handler fixed for one is
 * fixed for both. That is also why this list is actions rather than callbacks: a callback here
 * would be a copy of the routing that already exists.
 *
 * <p>Two of them are NOT plain actions and are handled by the panel directly, because they
 * never were toolbar actions:
 *
 * <ul>
 *   <li><b>Hide the price series</b> is one state of the view-mode CYCLE button, not a discrete
 *       command. Removing it means shortening the cycle and offering the state as its own switch —
 *       see {@code chartViewMode.js}.</li>
 *   <li><b>Zoom to the first calendar year</b> and <b>Show all trade cards</b> are new in this
 *       change and have no toolbar history at all.</li>
 * </ul>
 */

/*
 * ⚠ NO PRODUCT IMPORT ANY MORE. It existed for the per-product relocation exemptions, which were
 * retired on 2026-11-01 — a manifest states where each tool lives, per product, in one place
 * each. This file has never had a notion of the signed-in user's role and still does not.
 */

/**
 * THE THREE TABS, and what belongs in each.
 *
 * <h2>Declared here rather than implied by position in the JSX</h2>
 *
 * The panel was a flat scroll of sections, so which section a tool sat in was a property of where
 * its JSX happened to be. A reader adding a tool had to guess, and a tool could silently end up in
 * the wrong place by being pasted next to the wrong neighbour. A {@code tab} field on the tool is a
 * statement that can be read, tested and got wrong loudly — {@code adminPanelTabs.test.js} asserts
 * every tool names a tab that exists and that no tab is empty.
 *
 * <h2>⚠ TRENDPULSE IS IN CYCLES, and the brief's three names do not obviously hold it</h2>
 *
 * The brief lists "PTA, PTB, CTS, Spiral Calendar, cycle band configurations" for tab 1, "Gann
 * Confluence, Square of Nine, SK360, Time Projection" for tab 2 and system tooling for tab 3.
 * TrendPulse is a momentum curve with a phase ribbon: not price geometry and not system tooling, so
 * tab 1 is the closest fit of the three rather than an exact one. Stated because it is a judgement
 * and the next reader should know it was made rather than defaulted.
 */
export const TAB_CYCLES = 'cycles';
export const TAB_GEOMETRY = 'geometry';
export const TAB_SYSTEM = 'system';

/**
 * Every tab id, in panel order.
 *
 * <h2>⚠ `PANEL_TABS` IS DELETED, AND ITS LABELS HAD ALREADY DIVERGED</h2>
 *
 * It carried a LABEL per tab — `Cycle & Confluence`, `Price & Geometry`, `System & Access` —
 * while `admin.manifest.js` declares `Cycle & Confluence Studies`, `Price & Geometric Overlays`
 * and `System & Diagnostics`. Two declarations of one tab's name, already disagreeing, with the
 * manifest's being the ones a reader sees and this file's being the ones a test asserted.
 *
 * <p>So the labels are the manifest's and only the IDS stay here: both the manifest and the
 * component have to name a tab, and a shared constant is what stops them drifting apart by a
 * typo — which an id, unlike a label, cannot be caught doing by eye.
 */
export const PANEL_TAB_IDS = Object.freeze([TAB_CYCLES, TAB_GEOMETRY, TAB_SYSTEM]);

/**
 * The relocated tools that ARE toolbar actions.
 *
 * <p>Grouped by what a reader is looking for rather than by where they used to sit on the toolbar,
 * which is an ordering only the previous layout explains — and the headings are DERIVED from the
 * entries ({@link RELOCATED_GROUPS}), so adding one in a new group needs no second edit.
 *
 * <p>⚠ ORDER IS NOW PER TAB, not down one list. This said "Engines, then TrendPulse, then Scans,
 * then Chart tools", which was the flat panel's reading order and stopped being true when the
 * panel was tabbed — a tool's position is now its {@code tab} first and its place in this array
 * second. Corrected rather than left, because a stale ordering claim is the thing a reader trusts.
 *
 * <p>⚠ AND `Chart tools` WAS SPLIT, because it was a catch-all that straddled two tabs. The
 * Fibonacci projection is price geometry and a cycle length in bars is a cycle, so each took its
 * own heading; what is left under the old name is the three tools that really are chart tooling.
 * A group spanning two tabs prints the same sub-heading on both with different tools under each,
 * and the heading is the only navigation inside a tab — found by the test that asserts a group
 * belongs to exactly one tab, not by reading the layout.
 *
 * <p>No count here, deliberately. This said "the twelve relocated tools ... then the four general
 * tools" while the array held thirteen and five — a number in prose beside a list is a second
 * declaration of the list's length, and it goes stale on the first addition without anything
 * failing. Corrected 2026-10-20 by removing it rather than by bumping it.
 */
export const RELOCATED_TOOLS = [
  /*
   * ⚠ FOUR ENTRIES WERE REMOVED HERE ON 2026-11-01, because they had stopped being relocations.
   *
   * `cts_scan_2`, `cts_scan_3`, `open_intraday_animation` and `open_configuration_dialog` are on
   * a TOOLBAR under every manifest that carries them at all — the two orb scans on integralDelta
   * and the Studio, the animation and the colour dialog on all four. A "relocated" record for a
   * tool nothing relocates is a second, stale answer to where it lives, and this list is read as
   * the record of where a tool WENT.
   *
   * <p>⚠ TWO OF THEM ARE A RESTORED CUSTOMER CAPABILITY, worth stating rather than leaving in a
   * diff. Relocation had no per-product exemption for the colour dialog or the intraday
   * animation, and a customer product has no Admin Panel — so from the day they were relocated
   * until this one, a customer could reach NEITHER.
   */

  /*
   * THE THREE ONE-CLICK ENGINE RUNNERS, relocated 2026-10-19.
   *
   * They were already admin-only (ADMIN_ONLY_TOOLBAR_ACTIONS in nonAdminChrome.js), which is a
   * ROLE gate and a different question from this one: that hides them from a customer, this takes
   * them off the toolbar for everybody and puts them in the panel. Both gates stay — the role list
   * still names all six actions it documents, and removing one from it because the relocation now
   * also hides it would make that list a description of a layout that no longer exists.
   *
   * <p>Their ICONS entries in TabPanel are UNTOUCHED, and that is how relocation works here: the
   * toolbar filters at render through `isActionVisibleForProfile`, so the entry, its tooltip and
   * its `case` in the dispatcher all survive. Nothing was deleted and the panel invokes the same
   * handler the icon did.
   */
  {
    action: 'run_engine_pta',
    label: 'Run PTA',
    blurb: 'The Equator engine, with its locked parameters, painted on the chart. Long cycle.',
    group: 'Engines',
    tab: TAB_CYCLES,
  },
  {
    action: 'run_engine_ptb',
    label: 'Run PTB',
    blurb: 'The eight band rules, with their locked parameters, painted on the chart. Short cycle.',
    group: 'Engines',
    tab: TAB_CYCLES,
  },
  {
    action: 'run_engine_cts',
    label: 'Run CTS',
    blurb: 'The temporal pivot engine, with its locked parameters, painted on the chart.',
    group: 'Engines',
    tab: TAB_CYCLES,
  },
  {
    action: 'run_trendpulse',
    label: 'TrendPulse',
    blurb: 'Draw the momentum curve and its phase ribbon.',
    group: 'TrendPulse',
    tab: TAB_CYCLES,
  },
  {
    action: 'open_pattern_prediction_dialog',
    label: 'Pattern Prediction',
    blurb: 'Detect shapes on the TrendPulse curve and score them against price. Needs TrendPulse '
      + 'to have been run first — the shapes are detected on its curve.',
    group: 'TrendPulse',
    tab: TAB_CYCLES,
  },
  {
    action: 'evaluate_validation_lines',
    label: 'Validation Lines',
    blurb: 'Score every boundary shelf against 1×ATR(14) over five bars and mark each a success '
      + 'or a failure.',
    group: 'TrendPulse',
    tab: TAB_CYCLES,
  },
  /*
   * THE PORTFOLIO MANAGER, relocated 2026-10-20.
   *
   * In the ENGINES group rather than a section of its own, because it is the same subject as the
   * three runners above it: those answer "what did this strategy do" and this answers "what does
   * the BOOK do when several of them compete for one pot of capital". A one-item group would read
   * as a fifth heading for a fourth kind of thing, and the group header is the only navigation
   * this panel has.
   *
   * <p>It arrives already `adminOnly: true` on its toolbar entry, which is a ROLE gate and a
   * different question from this one — that hid it from a customer, this takes it off the bar for
   * everybody. Both stay: the flag is left on the entry, because removing it on the grounds that
   * the relocation now also hides it would make the entry's own declaration a description of a
   * layout that no longer exists.
   */
  {
    action: 'open_portfolio_manager',
    label: 'Portfolio Manager',
    blurb: 'Allocate one pot of capital across several strategies at once and see what the book '
      + 'does, rather than what any one of them does alone.',
    group: 'Engines',
    tab: TAB_CYCLES,
  },
  /*
   * ⚠ THE EIGHT-ICON UTILITY CLUSTER, relocated 2026-11-01 into SYSTEM & ACCESS.
   *
   * These sat between the drawing tools and the Admin Panel's own shield icon, and none of them is
   * an ephemeris overlay, a drawing tool or a core indicator — which is what the top bar is now
   * reserved for. They are diagnostic viewers, audit tools and system actions, so they belong on
   * the tab named for exactly that.
   *
   * <p>⚠ THREE OF THEM WERE `RETIRED_ACTIONS` FOR ONE DAY — DynaMan, the Workbench and Chart
   * Analytica. That mechanism took them off the toolbar and left them reachable only
   * programmatically, a cost recorded at the time. Relocation is the better answer to the same
   * instruction: same disappearance from the bar, and a button to press. The third list is gone.
   *
   * <p>Their ICONS entries, tooltips and dispatcher cases are all untouched, as relocation
   * requires — the toolbar filters at render, so restoring any of them to the bar is deleting one
   * entry from this array.
   */
  /*
   * ⚠ `open_configuration_dialog` IS NOT ADDED HERE — it was already relocated, further down this
   * same array as "Configuration — colours", in this same group and tab. Adding it again produced
   * a duplicate that the panel would have rendered twice; caught by the test that asserts
   * `RELOCATED_ACTIONS` has no repeats, which is exactly what that assertion is for.
   *
   * <p>It appeared on the toolbar in the screenshot this list was read from because the full
   * catalog was bypassing relocation. Nothing was wrong with its placement; the catalog was
   * showing a tool that already had a home.
   */
  {
    action: 'open_messages_dialog',
    label: 'Message Centre',
    blurb: 'Read the notices published to this deployment. Editable by an administrator, '
      + 'read-only for a customer.',
    group: 'Chart tools',
    tab: TAB_SYSTEM,
  },
  {
    action: 'toggle_simulate',
    label: 'Alpha Replay',
    blurb: 'Replay the chart with the future hidden. Click a bar to jump, or use the arrow keys '
      + 'to step one bar at a time.',
    group: 'Replay & simulation',
    tab: TAB_SYSTEM,
  },
  {
    action: 'open_dynaman_simulator',
    label: 'DynaMan Simulator',
    blurb: 'The research dialog: configure a run, measure it, and replay it on the chart.',
    group: 'Replay & simulation',
    tab: TAB_SYSTEM,
  },
  {
    action: 'launch_simulator',
    label: 'Verifiable Alpha Workbench',
    blurb: 'Replay a session bar by bar and trade against the engine with the future hidden, '
      + 'keeping a ledger of both books.',
    group: 'Replay & simulation',
    tab: TAB_SYSTEM,
  },
  {
    action: 'chart_analytica',
    label: 'Chart Analytica',
    blurb: 'Scan every bar from the oldest up to the last one and report the PTA/PTB, CTS, RSI, '
      + 'volume and Fibonacci statistics. Right-click a bar to scan to that bar instead.',
    group: 'Audit & scans',
    tab: TAB_SYSTEM,
  },
  {
    action: 'run_market_scanner',
    label: 'Market Scanner',
    blurb: 'Check every symbol in the market against the four top and four bottom lines of both '
      + 'PTA and PTB, within 0.25%, and list what is near one.',
    group: 'Audit & scans',
    tab: TAB_SYSTEM,
  },
  /*
   * ⚠ THE TWO BATCH SCANS, relocated 2026-11-01 into CYCLE & CONFLUENCE — and they were
   * SOFT-DELETED, not merely off the bar.
   *
   * Soft deletion hid them from every profile including Admin and left no route to them at all.
   * Relocation answers the same instruction better, so `SOFT_DELETED_ACTIONS` is now empty.
   *
   * <p>⚠ THESE TWO ARE NOT DISPATCHED ACTIONS. Every other entry in this array names a `case` in
   * `actionDispatcher.js`; these are self-contained COMPONENTS that own their button, their
   * dialog and their progress state (`GlobalScanButton`, `ChartAnalysisButton`). The panel renders
   * the component itself through its `slots` prop rather than a `Button` that calls `onAction` —
   * see `AdminPanelDialog`. Lifting their internal state out to make them dispatchable would be a
   * rewrite of two working controls for no gain.
   */
  {
    action: 'run_global_scan',
    label: 'Run Global Scan',
    blurb: 'Sweep every symbol in the market in one batch and store the result for the Daily Scan '
      + 'Viewer. Runs in the background.',
    group: 'Batch scans',
    tab: TAB_CYCLES,
    /*
     * ⚠ RENDERED BY A SLOT, NOT DISPATCHED. There is no `case` for this in `actionDispatcher.js`
     * and there must not be: the control is a self-contained component. Declared here so the
     * routability test can tell "deliberately not dispatched" from "lost in the move" -- that
     * test caught both of these the moment they were relocated, which is exactly its job.
     */
    slot: true,
  },
  {
    action: 'auto_analysis',
    label: 'Auto-Analysis',
    blurb: 'Run the Integral Delta analysis over this symbol and report what it accepted. Runs in '
      + 'the background.',
    group: 'Batch scans',
    tab: TAB_CYCLES,
    /*
     * ⚠ RENDERED BY A SLOT, NOT DISPATCHED. There is no `case` for this in `actionDispatcher.js`
     * and there must not be: the control is a self-contained component. Declared here so the
     * routability test can tell "deliberately not dispatched" from "lost in the move" -- that
     * test caught both of these the moment they were relocated, which is exactly its job.
     */
    slot: true,
  },
  {
    action: 'toggle_recent_fib_projections',
    label: 'Recent Pivot Fib Projections',
    blurb: 'Anchor to the two most recent five-bar pivots and project Fibonacci multiples of that '
      + 'interval forward. Click again to remove.',
    group: 'Fibonacci',
    tab: TAB_GEOMETRY,
  },
  {
    action: 'open_fixed_cycle_dialog',
    label: 'Fixed Length Cycles',
    blurb: 'Enter a cycle length in bars, then click a bar to anchor it.',
    group: 'Cycle tools',
    tab: TAB_CYCLES,
  },
  {
    action: 'open_chart_comparison',
    label: 'Compare Two Time Periods',
    blurb: 'Put two stretches of the chart side by side or overlaid.',
    group: 'Chart tools',
    tab: TAB_SYSTEM,
  },
];

/**
 * Every action the toolbar must now hide.
 *
 * <p>Derived, never written out a second time. A hand-copied set is the drift this whole file
 * exists to prevent.
 */
export const RELOCATED_ACTIONS = RELOCATED_TOOLS.map((t) => t.action);

/*
 * ⚠ THE PER-PRODUCT RELOCATION BYPASS MAP IS GONE — `RELOCATION_EXEMPT_BY_PRODUCT`, retired
 * 2026-11-01 and then DELETED rather than left empty.
 *
 * It let one product keep a tool on the bar that another had relocated. That is a per-product
 * statement about where a tool lives, which is exactly what a manifest's `toolbar` and
 * `adminPanel.tabs` now say directly — so the map was a second answer, reachable only through
 * this file, to a question each manifest answers once.
 *
 * <p>⚠ IT SURVIVED ONE COMMIT AS `Object.freeze({})` AND THAT WAS THE WRONG CALL. An empty map
 * still read by `isRelocated` is a lookup that can never match: the mechanism reads as live, a
 * reader adding an entry would see no effect, and the constant was still EXPORTED so a second
 * module could have started depending on a bypass the architecture no longer honours. Deleting
 * the export and the lookup together is what makes the retirement checkable.
 *
 * <p>Nothing was lost in the collapse. The two CTS orbs are named on integralDelta's toolbar; the
 * other three were Studio-only tools that no customer manifest carries at all.
 *
 * ⚠ WHAT THE EXEMPTIONS SAID, kept as prose because the REASONS are not disposable — two of them
 * exist to stop the UI becoming stricter than the server, a constraint the manifests inherit and
 * must keep honouring:
 *   cts_scan_2, cts_scan_3   on integralDelta all three orbs sit together on the bar, one
 *                            triangle per orb badged 1/2/3. Now: named in that manifest.
 *   open_messages_dialog     the Message Centre is a read-only notice board for CUSTOMERS with
 *                            no other route to it, and hiding it has a recorded cost.
 *   toggle_simulate          Alpha Replay, deliberately customer-facing on integralDelta.
 *   run_market_scanner       ⚠ THE SERVER STILL PERMITS AN INTEGRALDELTA CUSTOMER, so relocating
 *                            it on every product made the UI stricter than the backend —
 *                            `productVisibility.test.js` has a block on the UI gate mirroring
 *                            the server gate, written after the cycle pills shipped the
 *                            mirror-image defect: two buttons that 403'd on every click.
 *
 * ⚠ THE LAST THREE ARE STUDIO-ONLY UNDER THE MANIFESTS, which is a NARROWING for an
 * integralDelta customer and is stated rather than left in a diff. If the Message Centre or the
 * Market Scanner should reach that product's customers, the fix is one id in
 * `integraldelta.manifest.js` — and for the scanner the server's own answer says it should.
 */

/**
 * Whether {@code action} has an Admin Panel home.
 *
 * <p>⚠ THE SECOND ARGUMENT IS ACCEPTED AND IGNORED. Every call site passes a product, and keeping
 * the signature means none of them had to change in a refactor whose subject was elsewhere — but
 * the answer is now the same for every product, because WHERE a tool lives is a manifest's
 * statement and a per-product bypass here would be a second one.
 *
 * <p>Kept rather than dropped from the signature so a caller cannot silently start getting a
 * one-argument answer it believes is product-aware. `productVisibility.test.js` asserts the
 * answer is invariant across all four targets and across an unknown one.
 */
export const isRelocated = (action, product) => { // eslint-disable-line no-unused-vars
  if (!RELOCATED_ACTIONS.includes(action)) return false;
  return true;
};

/**
 * The tools the Admin Panel should list for {@code product}.
 *
 * <p>Exists so a tool cannot be on the toolbar AND in the panel at once. The panel reading the
 * raw array would print CTS2 and CTS3 on integralDelta beside the very triangles that now carry
 * them — two routes to one scan, one of which the reader has to be told is redundant.
 */
export const relocatedToolsFor = (product) =>
  RELOCATED_TOOLS.filter((t) => isRelocated(t.action, product));

/*
 * ⚠ `RELOCATED_GROUPS` AND `relocatedGroupsFor` ARE DELETED, and so are `panelToolsForTab`,
 * `panelGroupsForTab` and `allPanelToolsFor` below.
 *
 * All five derived the PANEL'S STRUCTURE from the `tab` and `group` fields on twenty-five
 * scattered descriptors. `admin.manifest.js` declares that structure directly — tabs, their
 * sub-headings, and the tools under each — because membership and layout are one decision and a
 * derivation is a second answer to it.
 *
 * <p>⚠ DELETED RATHER THAN LEFT UNUSED, which is the same call the empty bypass map got above.
 * A reader finding `panelToolsForTab` would reasonably use it, get an answer built from the
 * descriptor fields, and never learn that the panel renders from somewhere else — and the two
 * would agree until the day somebody moved a tool in the manifest alone.
 *
 * <p>The `tab` and `group` fields themselves are KEPT on the entries: they are the record of
 * where a tool WENT when it came off the toolbar, which is history worth having, and this file's
 * own tests read them.
 */

/**
 * TOOLS THAT LIVE ONLY IN THE PANEL — never on the toolbar, so never RELOCATED.
 *
 * <h2>⚠ A SEPARATE LIST, and that is not tidiness</h2>
 *
 * {@link RELOCATED_ACTIONS} is what the TOOLBAR HIDES. Putting a panel-native action in it would
 * tell the toolbar to hide something that was never there — harmless in effect, and a false
 * statement in the one list a reader consults to find out what moved. {@code adminPanelTools.test.js}
 * asserts {@code RELOCATED_ACTIONS} is exactly the brief-named relocation set, so it would also
 * fail, correctly.
 *
 * <p>They are still dispatched the same way: each carries an {@code action} the panel passes to
 * {@code onAction}, which is the toolbar's own {@code handleIconClick}. So there is still one
 * routing table, and a handler fixed for one caller is fixed for both.
 */
export const PANEL_TOOLS = [
  {
    action: 'toggle_sk360',
    label: 'SK360 — 360° price projection',
    blurb: 'From the last clicked bar: one full turn of the square-of-nine wheel. Two lines to '
      + '(√C ± 2)² a year ahead. Click again to remove.',
    group: 'Gann geometry',
    tab: TAB_GEOMETRY,
  },
  {
    action: 'toggle_time_projection',
    label: 'Time Projection — price as days',
    blurb: "The close's own digits read as calendar-day offsets — the first 2, 3 and 4 of them. "
      + 'Markers on the dates they land on, dropped when they fall past the chart. Click again '
      + 'to remove.',
    group: 'Gann geometry',
    tab: TAB_GEOMETRY,
  },
  {
    action: 'toggle_sk_grid',
    label: 'SK-Grid — harmonic price ladder',
    blurb: 'Eight horizontal levels at (√C + ¼…2)², held for a year from the clicked bar. Every '
      + 'rung is ABOVE the close — the step is positive, so this is a one-sided ladder. Click '
      + 'again to remove.',
    group: 'Gann geometry',
    tab: TAB_GEOMETRY,
    /*
     * ⚠ THE ONLY TOOL HERE THAT REFUSES TO GUESS ITS ANCHOR.
     *
     * SK360 and Time Projection fall back to the last bar, so they are useful on first press.
     * This one is specified as disabled until a bar is clicked — so the panel greys it and says
     * why, and {@code buildSkGrid} refuses an absent anchor rather than falling back. Both halves
     * are needed: a disabled button with a fallback behind it is a second, invisible answer to
     * "what is this anchored to", and a fallback with no button guard is a tool that silently
     * anchors somewhere the reader did not choose.
     */
    needsAnchor: true,
  },
];

/**
 * The tools that cannot run without a clicked bar.
 *
 * <p>Derived, so the panel's guard and the declaration cannot disagree — the same rule
 * {@link RELOCATED_ACTIONS} follows for the toolbar.
 */
export const ANCHOR_REQUIRED_ACTIONS = PANEL_TOOLS
  .filter((t) => t.needsAnchor)
  .map((t) => t.action);


/**
 * THE PER-TOOL DETAIL THE PANEL RENDERS A ROW FROM, keyed by dispatcher action.
 *
 * <h2>⚠ THE `tab` AND `group` FIELDS ARE NO LONGER READ BY THE PANEL</h2>
 *
 * {@code admin.manifest.js} owns which tab a tool is on and under what heading, because
 * membership and layout are one decision and they were previously spread across two dozen
 * scattered objects. What a DESCRIPTOR is for is the prose and the per-tool guard: the label,
 * the blurb, and {@code needsAnchor}.
 *
 * <p>The two fields are KEPT on the entries rather than stripped, deliberately: they are what
 * {@link relocatedToolsFor} and this file's own tests use to record where a tool WENT when it
 * came off the toolbar, which is history worth having.
 *
 * <p>⚠ KEYED BY ACTION, NOT BY TOOL ID. These entries predate the registry and name dispatcher
 * actions; the manifest names tool ids. `AdminPanelDialog` resolves one to the other through the
 * registry, which is the single place those two vocabularies meet.
 */
export const PANEL_TOOL_DETAIL = Object.freeze(Object.fromEntries(
  [...RELOCATED_TOOLS, ...PANEL_TOOLS].map((t) => [t.action, Object.freeze({
    label: t.label, blurb: t.blurb, needsAnchor: !!t.needsAnchor, slot: !!t.slot,
  })]),
));


/** The toolbar entry that opens the panel. Admin-only, enforced in two places — see TabPanel. */
export const ADMIN_PANEL_ACTION = 'open_admin_panel';
